/*
 * ICOFFEE accounts: shared client for the sign-in modal, header menu, wishlist hearts and account pages.
 * Identity is Shopify's own customer accounts (email code, Google, Facebook). Profile, address and
 * wishlist writes go through the ICOFFEE Accounts app proxy at /apps/icoffee, which only ever acts
 * on the signed-in customer.
 */
const config = window.IcAccountConfig || {};
const t = config.strings || {};
const store = {
  get(key, fallback) {
    try {
      const v = localStorage.getItem(key);
      return v ? JSON.parse(v) : fallback;
    } catch {
      return fallback;
    }
  },
  set(key, value) {
    try {
      if (value === null) localStorage.removeItem(key);
      else localStorage.setItem(key, JSON.stringify(value));
    } catch {}
  },
};

const format = (str, vars = {}) => String(str || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => (k in vars ? vars[k] : ''));

/* ---------- app proxy ---------- */

async function api(route, { method = 'GET', body } = {}) {
  const res = await fetch(`${config.proxy}/${route}`, {
    method,
    credentials: 'same-origin',
    headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try {
    data = await res.json();
  } catch {}
  if (!res.ok || !data || data.ok === false) {
    const error = new Error((data && data.error) || `http_${res.status}`);
    error.code = (data && data.error) || `http_${res.status}`;
    error.data = data;
    throw error;
  }
  return data;
}

/* ---------- toasts ---------- */

function toast(message, { tone = 'success', action, duration = 4200 } = {}) {
  const host = document.querySelector('[data-ic-toasts]');
  if (!host || !message) return;
  const el = document.createElement('div');
  el.className = `ic-toast ic-toast--${tone}`;
  el.setAttribute('role', tone === 'error' ? 'alert' : 'status');
  const icon = tone === 'error'
    ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7.5v5.5M12 16.5v.01"/></svg>'
    : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="m8 12.5 2.8 2.8L16.5 9"/></svg>';
  el.innerHTML = `${icon}<span class="ic-toast__text"></span>`;
  el.querySelector('.ic-toast__text').textContent = message;
  if (action) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ic-toast__action';
    btn.textContent = action.label;
    btn.addEventListener('click', () => {
      action.onClick();
      dismiss();
    });
    el.appendChild(btn);
  }
  host.appendChild(el);
  requestAnimationFrame(() => el.classList.add('is-in'));
  let timer = setTimeout(dismiss, duration);
  el.addEventListener('pointerenter', () => clearTimeout(timer));
  el.addEventListener('pointerleave', () => (timer = setTimeout(dismiss, 1800)));
  function dismiss() {
    clearTimeout(timer);
    el.classList.remove('is-in');
    el.classList.add('is-out');
    setTimeout(() => el.remove(), 260);
  }
  return dismiss;
}

/* ---------- cart: add lines the way Horizon's product form does, so the drawer and bubble update ---------- */

async function addToCart(items) {
  const lines = items.filter((i) => i.id).map((i) => ({ id: Number(i.id), quantity: i.quantity || 1 }));
  if (!lines.length) return;
  // the ICOFFEE cart drawer (assets/ic-cart.js) handles the request, re-render and opening
  if (window.IcCart) return window.IcCart.add(lines);
  let events = null;
  try {
    events = await import('@shopify/events');
  } catch {}
  const sectionIds = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);
  const deferred = events ? events.CartLinesUpdateEvent.createPromise() : null;
  if (events) {
    document.dispatchEvent(
      new events.CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lines.map((l) => ({ merchandiseId: String(l.id), quantity: l.quantity })),
        promise: deferred.promise,
      })
    );
  }
  try {
    const res = await fetch(`${config.cartAddUrl}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: lines, sections: sectionIds.join(','), sections_url: location.pathname }),
    });
    const data = await res.json();
    if (data.status) throw new Error(data.description || data.message || 'add_failed');
    const cart = await (await fetch(`${config.cartUrl}.js`, { headers: { Accept: 'application/json' } })).json();
    if (deferred) {
      deferred.resolve({
        cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: data.items, sections: data.sections, itemCount: cart.item_count, source: 'ic-account' },
      });
      // show what was added: the theme keeps the drawer closed on add, but here the shopper stays on the account page
      const drawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
      if (drawer && typeof drawer.open === 'function') setTimeout(() => !drawer.isOpen && drawer.open(), 250);
    } else {
      location.href = config.cartUrl;
    }
    return cart;
  } catch (err) {
    if (deferred) deferred.reject(err);
    toast(err.message && err.message !== 'add_failed' ? err.message : t.generic_error, { tone: 'error' });
    throw err;
  }
}

/* ---------- wishlist ---------- */

const GUEST_KEY = 'ic:wishlist';
const wishlist = {
  ids: new Set((config.loggedIn ? config.wishlist : store.get(GUEST_KEY, [])).map(String)),
  has(id) {
    return this.ids.has(String(id));
  },
  sync() {
    document.querySelectorAll('[data-ic-wish-id]').forEach((btn) => {
      btn.setAttribute('aria-pressed', String(this.has(btn.dataset.icWishId)));
    });
    document.dispatchEvent(new CustomEvent('ic:wishlist', { detail: { ids: [...this.ids] } }));
  },
  async set(id, on, { silent = false } = {}) {
    id = String(id);
    if (on === this.has(id)) return;
    on ? this.ids.add(id) : this.ids.delete(id);
    this.sync();
    if (!config.loggedIn) {
      store.set(GUEST_KEY, [...this.ids]);
      if (!silent && on) toast(t.wish_added, { action: { label: t.sign_in, onClick: () => openAuth('signin') } });
      return;
    }
    try {
      const data = await api('wishlist', { method: 'POST', body: on ? { add: [id] } : { remove: [id] } });
      this.ids = new Set(data.wishlist.map((g) => g.split('/').pop()));
      this.sync();
      if (!silent) toast(on ? t.wish_added : t.wish_removed, on ? {} : { action: { label: t.undo, onClick: () => this.set(id, true) } });
    } catch (err) {
      on ? this.ids.delete(id) : this.ids.add(id);
      this.sync();
      handleError(err);
    }
  },
  toggle(id) {
    return this.set(id, !this.has(id));
  },
};

document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-ic-wish-id]');
  if (!btn) return;
  e.preventDefault();
  wishlist.toggle(btn.dataset.icWishId);
});

/* ---------- auth ---------- */

function openAuth(view = 'signin') {
  const dialog = document.getElementById('IcAuth');
  if (dialog && dialog.icOpen) dialog.icOpen(view);
}

document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-ic-auth-open]');
  if (!trigger) return;
  e.preventDefault();
  const drawer = trigger.closest('dialog[open]');
  if (drawer && drawer.id !== 'IcAuth') drawer.close();
  openAuth(trigger.dataset.icAuthOpen || 'signin');
});

function handleError(err) {
  if (err && err.code === 'signed_out') {
    toast(t.session_error, { tone: 'error', action: { label: t.sign_in, onClick: () => (location.href = config.loginUrl) } });
  } else {
    toast(t.generic_error, { tone: 'error' });
  }
}

const PENDING_KEY = 'ic:pending-profile';
const INTENT_KEY = 'ic:auth-intent';
const DAY = 864e5;

// Runs once after the customer comes back from Shopify's sign-in: save the sign-up details, merge the guest wishlist.
async function afterSignIn() {
  const pending = store.get(PENDING_KEY, null);
  const intent = store.get(INTENT_KEY, null);
  store.set(INTENT_KEY, null);
  const sameEmail = (a, b) => !a || !b || a.trim().toLowerCase() === b.trim().toLowerCase();

  if (pending && Date.now() - pending.ts < 2 * DAY && sameEmail(pending.email, config.email)) {
    try {
      const body = { name: pending.name };
      if (pending.phone) body.phone = pending.phone;
      if (typeof pending.marketing === 'boolean') body.marketing = pending.marketing;
      try {
        await api('profile', { method: 'POST', body });
      } catch (err) {
        if (err.code !== 'phone_taken' && err.code !== 'invalid' && err.code !== 'invalid_phone') throw err;
        await api('profile', { method: 'POST', body: { name: pending.name } });
        toast(t.phone_taken, { tone: 'error', duration: 7000 });
      }
      store.set(PENDING_KEY, null);
      const first = pending.name.split(/\s+/)[0];
      toast(format(t.welcome, { name: first }));
      document.querySelectorAll('[data-ic-first-name]').forEach((el) => (el.textContent = first));
      document.querySelectorAll('[data-ic-full-name]').forEach((el) => (el.textContent = pending.name));
      document.querySelectorAll('[data-ic-profile-name]').forEach((el) => { if (!el.value || el.value.includes('@')) el.value = pending.name; });
      document.querySelectorAll('[data-ic-profile-phone]').forEach((el) => { if (!el.value && pending.phone) el.value = pending.phone; el.dispatchEvent(new Event('ic:reset')); });
    } catch {
      // app proxy unreachable: keep the pending details and try again on the next page
    }
  } else if (pending && Date.now() - pending.ts >= 2 * DAY) {
    store.set(PENDING_KEY, null);
  } else if (intent && Date.now() - intent.ts < DAY) {
    toast(t.welcome_back);
  }

  const guest = store.get(GUEST_KEY, []);
  if (guest.length) {
    try {
      const data = await api('wishlist', { method: 'POST', body: { add: guest } });
      store.set(GUEST_KEY, null);
      wishlist.ids = new Set(data.wishlist.map((g) => g.split('/').pop()));
      wishlist.sync();
    } catch {}
  }
}

/* ---------- avatar: swap every [data-ic-avatar-slot] on the page (header, sidebar, picker) ---------- */

function avatarSrc(avatar, size) {
  if (!avatar) return '';
  if (avatar.type === 'preset' && avatar.id) return String(config.avatarAsset || '').replace('ic-avatar-bean', `ic-avatar-${avatar.id}`);
  if (avatar.type === 'image' && avatar.url) {
    const px = size * 2;
    return `${avatar.url}${avatar.url.includes('?') ? '&' : '?'}width=${px}&height=${px}&crop=center`;
  }
  return '';
}
function setAvatar(avatar) {
  document.querySelectorAll('[data-ic-avatar-slot]').forEach((slot) => {
    const src = avatarSrc(avatar, Number(slot.dataset.size) || 40);
    const img = slot.querySelector('.ic-avatar-slot__img');
    if (src) img.src = src;
    img.hidden = !src;
    slot.toggleAttribute('data-has-avatar', !!src);
    slot.classList.remove('is-pop');
    void slot.offsetWidth;
    if (src) slot.classList.add('is-pop');
  });
  document.dispatchEvent(new CustomEvent('ic:avatar', { detail: { avatar } }));
}

window.IcAccount = { config, api, toast, addToCart, wishlist, openAuth, handleError, format, store, setAvatar, PENDING_KEY, INTENT_KEY };
document.dispatchEvent(new CustomEvent('ic:account-ready'));

wishlist.sync();
if (config.loggedIn) afterSignIn();

// re-sync hearts rendered later (theme editor reloads, section re-renders)
new MutationObserver(() => {
  document.querySelectorAll('[data-ic-wish-id]:not([data-ic-wish-synced])').forEach((btn) => {
    btn.dataset.icWishSynced = '';
    btn.setAttribute('aria-pressed', String(wishlist.has(btn.dataset.icWishId)));
  });
}).observe(document.body, { childList: true, subtree: true });
